// Shared invite modal for circles + networks. Mounted from CircleDetailView
// and NetworkDetailView. Sections (in order):
//   1 — Invite member (typeahead, in-app notifications on accept)
//   2 — Invite as leader (single batch-level checkbox)
//   3 — Invite by email (comma-separated chips, per-email status probe)
//       + Personal note (included in the email)
//
// Invite-by-link is gone — the server only supports per-recipient invites.
// All recipients in one batch share the same `roleOnAccept`, controlled by
// the "Invite as leader" checkbox above the email input.
//
// Group kind drives only which API routes the fetchers + mutations hit
// (`/api/v1/circles/{id}/*` vs `/api/v1/networks/{id}/*`); the underlying
// surfaces are identical (lifted into shared services on the server in
// `service/group-invites.ts`).
//
// After Send, the modal switches to a result step that surfaces every
// outcome — created invites, on-platform users that got auto-added, and
// recipients the server skipped (duplicate/already_member/invalid). The old
// modal closed on success and dropped `skipped` on the floor, which
// translated to silent "invite never arrived" support tickets.

import {
  useEffect,
  useMemo,
  useRef,
  useState,
  type ClipboardEvent,
  type KeyboardEvent,
  type ReactNode,
} from 'react';
import { useQuery } from '@tanstack/react-query';
import { useTranslations } from '@/i18n/translations';
// Single-feature glyph (chip status dot for "invite already pending"). Per
// `@leanin/ui` icon convention, one-off glyphs live with the feature that
// uses them rather than expanding the curated `I` map.
import { Clock as ClockGlyph, ChevronUp as ArrowUpGlyph } from 'lucide-react';
import {
  AlertDialog,
  Avatar,
  Body,
  Button,
  Checkbox,
  Dialog,
  DialogFooter,
  DialogSection,
  Field,
  Heading,
  I,
  Icon,
  Input,
  MultiCombobox,
  Notice,
  PersonRow,
  Pill,
  Row,
  Stack,
  Textarea,
  TONE,
  Tooltip,
  type ComboboxOption,
} from '@leanin/ui';
import type { components } from '@leanin/api-client';
import { INVITE_BATCH_CAP, isValidEmailAddress } from '@leanin/shared/email/address';
import { DomainError } from '@leanin/shared/errors';
import {
  fetchInvitableMembers,
  fetchInviteEmailStatus,
  type GroupKind,
  type InvitableMemberRelationship,
  type InviteEmailStatus,
  groupKeys,
} from '../../lib/queries/groups';
import { useCreateGroupInvites, useSetGroupInviteNote } from '../../lib/mutations/groups';
import { useErrorMessage, type DescribeError } from '../../lib/error-message';
import { readStorage, removeStorage } from '../../lib/browser-storage';
import { formatPersonRole } from '../../lib/person-role';
import { useResetOnClose } from '../circles/hooks/use-reset-on-close';
import { CopyJoinLinkButton } from './admin/CopyJoinLinkButton';

const SEARCH_DEBOUNCE_MS = 200;

// Pasted recipient lists arrive in mail-client shapes: comma / semicolon /
// newline separated, often as `Name <email>` pairs. Split on unambiguous
// separators only — a lone malformed string ("jane doe@gmail.com") stays
// intact so it fails validation visibly instead of being silently mangled
// into a different address.
function splitPastedRecipients(text: string): string[] {
  const out: string[] = [];
  const push = (raw: string) => {
    const cleaned = raw
      .replace(/^mailto:/i, '')
      .replace(/\?.*$/, '') // mailto query ("?subject=…")
      .replace(/[.,;:)\]>]+$/, ''); // list punctuation, stray closers
    if (cleaned.length > 0) out.push(cleaned);
  };
  // Quoted display names ("Last, First" <email>) hide commas from the
  // separator split — drop the quoted runs; the address survives. The
  // (?!@) guard leaves RFC quoted-local-part addresses ("j..d"@x.com)
  // intact so they fail validation as their full original text instead
  // of a confusing "@x.com" residue.
  const unquoted = text.replace(/"[^"]*"(?!@)/g, ' ');
  for (const part of unquoted.split(/[\n\r\t,;]+/)) {
    const trimmed = part
      .trim()
      // leading bullets / list numbering: "• x", "- x", "1. x", "2) x"
      .replace(/^(?:[•·*-]|\d+[.)])\s+/, '');
    if (trimmed.length === 0) continue;
    // All bracketed addresses on the line, not just the first — bare
    // pairs ("<a@x.com> <b@x.com>") have no separator for the split
    // above, and taking only the first silently dropped the rest.
    const wrapped = [...trimmed.matchAll(/[<(]([^<>()\s]+@[^<>()\s]+)[>)]/g)];
    if (wrapped.length > 0) {
      for (const m of wrapped) {
        if (m[1] !== undefined) push(m[1]);
      }
      continue;
    }
    // Contact-list lines: "Arianna Huffington - arianna@thriveglobal.com".
    // Extract only when an explicit name/address separator is present — a
    // space alone ("jane doe@gmail.com") is more likely a typo'd address,
    // and guessing there would mail the wrong person. No-separator lines
    // fall through and fail visibly instead.
    const dashed = trimmed.match(/^.*\S\s+[-–—:]\s*(\S+@\S+)$/);
    if (dashed?.[1] !== undefined) {
      push(dashed[1]);
      continue;
    }
    const words = trimmed.split(/\s+/);
    if (words.length > 1 && words.every((w) => w.includes('@'))) {
      for (const w of words) push(w);
      continue;
    }
    push(trimmed);
  }
  return out;
}

// Maps a VALIDATION envelope's issue paths (`emails.3`) back to the
// addresses in the submitted batch so the offending chips can be flagged.
function invalidEmailsFromDetails(
  details: Record<string, string[]>,
  sentEmails: readonly string[],
): string[] {
  const bad = new Set<string>();
  for (const path of Object.keys(details)) {
    const m = /^emails\.(\d+)$/.exec(path);
    if (m?.[1] === undefined) continue;
    const email = sentEmails[Number(m[1])];
    if (email !== undefined) bad.add(email);
  }
  return [...bad];
}

// Known send-failure codes get plain-language copy; codes in the shared
// `Errors` catalog get that copy; everything else falls back to the generic
// line with the raw code for support triage.
const SEND_FAILURE_KEYS: Record<
  string,
  'invite.sendFailure.archived' | 'invite.sendFailure.memberCap' | 'invite.sendFailure.timeout'
> = {
  'groups/group_archived': 'invite.sendFailure.archived',
  'groups/member_cap_reached': 'invite.sendFailure.memberCap',
  // Gateway / infra failures (no app error envelope, surfaced as http_<status>
  // by throwApiError). A large batch that trips an upstream timeout lands here
  // — steer the leader at the workaround instead of an opaque code.
  http_408: 'invite.sendFailure.timeout',
  http_502: 'invite.sendFailure.timeout',
  http_503: 'invite.sendFailure.timeout',
  http_504: 'invite.sendFailure.timeout',
};

function sendFailureMessage(
  err: unknown,
  describe: DescribeError,
  t: ReturnType<typeof useTranslations<'Group'>>,
): string {
  if (err instanceof DomainError) {
    const key = SEND_FAILURE_KEYS[err.error.code];
    if (key !== undefined) return t(key);
    return describe(err, t('invite.sendFailure.withCode', { code: err.error.code }));
  }
  return t('invite.sendFailure.generic');
}

type CreateInvitesResult = components['schemas']['CreateMembershipInvitesResponse'];
type PerEmailOutcome = CreateInvitesResult['results'][number];

export type InviteMemberModalProps = {
  readonly open: boolean;
  readonly onOpenChange: (open: boolean) => void;
  readonly kind: GroupKind;
  readonly groupId: string;
  readonly groupName: string;
};

type PickedMember = {
  readonly userId: string;
  readonly email: string;
  readonly name: string;
  readonly photoUrl: string | null;
  readonly jobTitle: string | null;
  readonly company: string | null;
};

type TypeaheadHit = {
  readonly userId: string;
  readonly firstName: string;
  readonly lastName: string;
  readonly email: string;
  readonly photoUrl: string | null;
  readonly jobTitle: string | null;
  readonly company: string | null;
  readonly relationship: InvitableMemberRelationship;
};

// Per-chip status, projected from the server's `InviteEmailStatus` union.
// `pending` is the in-flight state; `error` is the soft-fail fallback so the
// chip stays sendable (the create endpoint will surface a hard error on
// submit if it can't proceed).
type EmailMatchState =
  | 'pending'
  | { readonly kind: 'invalid_email' }
  | { readonly kind: 'off_platform' }
  | {
      readonly kind: 'on_platform_invitable';
      readonly name: string;
      readonly sublabel: string;
      readonly photoUrl: string | null;
    }
  | {
      readonly kind: 'already_member';
      readonly name: string;
      readonly sublabel: string;
      readonly role: 'member' | 'leader';
      readonly photoUrl: string | null;
    }
  | {
      readonly kind: 'pending_invite';
      readonly inviterName: string;
      readonly sentAtIso: string;
    }
  | {
      readonly kind: 'pending_request';
      readonly requestedAtIso: string;
    }
  | { readonly kind: 'error' };

// A chip in a blocking state is excluded from the submit payload and counted
// as "will be skipped" in the summary. `already_member` is the nuanced one:
// a member-role recipient invited as leader is NOT blocking — the server
// promotes them in place. Note: blocking ≠ button-disabling. Benign skips
// (already a member, already invited) leave Send enabled and are simply not
// sent — only `invalid_email` chips disable Send, since they need the
// sender's correction. Disabling on benign skips dead-ends the common
// "re-paste the same list" flow against the "N ready to invite" copy.
function isBlockingState(
  state: EmailMatchState | undefined,
  roleOnAccept: 'member' | 'leader',
): boolean {
  if (state === undefined || state === 'pending') return false;
  if (
    state.kind === 'invalid_email' ||
    state.kind === 'pending_invite' ||
    state.kind === 'pending_request'
  ) {
    return true;
  }
  if (state.kind === 'already_member') {
    // Promote case is NOT blocking — the server handles it in-tx.
    return !(state.role === 'member' && roleOnAccept === 'leader');
  }
  return false;
}

function projectStatus(s: InviteEmailStatus): EmailMatchState {
  switch (s.status) {
    case 'invalid_email':
      return { kind: 'invalid_email' };
    case 'off_platform':
      return { kind: 'off_platform' };
    case 'on_platform_invitable': {
      const name = fullName(s.member.firstName, s.member.lastName);
      const sublabel = formatPersonRole(s.member.jobTitle, s.member.company);
      return {
        kind: 'on_platform_invitable',
        name,
        sublabel,
        photoUrl: s.member.photoUrl,
      };
    }
    case 'already_member': {
      const name = fullName(s.member.firstName, s.member.lastName);
      const sublabel = formatPersonRole(s.member.jobTitle, s.member.company);
      return {
        kind: 'already_member',
        name,
        sublabel,
        role: s.role,
        photoUrl: s.member.photoUrl,
      };
    }
    case 'pending_invite': {
      // Empty inviterName signals a system-issued invite (no human
      // inviter); the consumer at line 939 already branches on
      // `inviterName.length > 0` to fall back to a generic message.
      const inviterName = s.invite.invitedBy
        ? fullName(s.invite.invitedBy.firstName, s.invite.invitedBy.lastName)
        : '';
      return {
        kind: 'pending_invite',
        inviterName,
        sentAtIso: s.invite.createdAt,
      };
    }
    case 'pending_request':
      return { kind: 'pending_request', requestedAtIso: s.requestedAt };
  }
}

function fullName(first: string, last: string): string {
  return [first, last]
    .filter((p) => p.length > 0)
    .join(' ')
    .trim();
}

// "5 days ago" / "today" style relative formatter for pending-invite hints.
// Short and approximate is fine here — exact timestamps live in the leader's
// pending-invites admin list, not on this chip.
function relativeDays(iso: string, t: ReturnType<typeof useTranslations<'Group'>>): string {
  const then = new Date(iso).getTime();
  if (!Number.isFinite(then)) return '';
  const days = Math.max(0, Math.round((Date.now() - then) / 86_400_000));
  if (days === 0) return t('invite.relativeDays.today');
  if (days === 1) return t('invite.relativeDays.yesterday');
  if (days < 7) return t('invite.relativeDays.daysAgo', { count: days });
  if (days < 30) return t('invite.relativeDays.weeksAgo', { count: Math.round(days / 7) });
  return t('invite.relativeDays.monthsAgo', { count: Math.round(days / 30) });
}

// Trailing status for a name-typeahead row whose subject already has standing
// in the group. Returns null for `invitable` (a normal, selectable row).
// Copy mirrors the email chip's `chipVisual` so the two surfaces agree.
function relationshipStatus(
  rel: InvitableMemberRelationship,
  kind: GroupKind,
  t: ReturnType<typeof useTranslations<'Group'>>,
): { readonly label: string; readonly tooltip: string; readonly glyph: ReactNode } | null {
  switch (rel.kind) {
    case 'invitable':
      return null;
    case 'already_member':
      return {
        label:
          rel.role === 'leader'
            ? t('invite.relationship.leaderLabel')
            : t('invite.relationship.memberLabel'),
        tooltip: t('invite.relationship.alreadyTooltip', { role: rel.role, kind }),
        glyph: I.check,
      };
    case 'pending_invite':
      return {
        label: t('invite.relationship.invitedLabel'),
        tooltip:
          rel.invitedByName !== null && rel.invitedByName.length > 0
            ? t('invite.relationship.invitedByTooltip', {
                time: relativeDays(rel.sentAt, t),
                name: rel.invitedByName,
              })
            : t('invite.relationship.invitedTooltip', { time: relativeDays(rel.sentAt, t) }),
        glyph: <ClockGlyph size={12} strokeWidth={1.75} />,
      };
    case 'pending_request':
      return {
        label: t('invite.relationship.requestedLabel'),
        tooltip: t('invite.relationship.requestedTooltip', {
          kind,
          time: relativeDays(rel.requestedAt, t),
        }),
        glyph: <ClockGlyph size={12} strokeWidth={1.75} />,
      };
  }
}

export function InviteMemberModal({
  open,
  onOpenChange,
  kind,
  groupId,
  groupName,
}: InviteMemberModalProps) {
  const t = useTranslations('Group');
  const describeError = useErrorMessage();
  const sendInvitesMutation = useCreateGroupInvites();

  // Two-step modal: compose -> result. The result step is held in component
  // state so the user can read the per-recipient breakdown before either
  // closing or going back to compose another batch.
  const [step, setStep] = useState<'compose' | 'result'>('compose');
  const [result, setResult] = useState<CreateInvitesResult | null>(null);

  const [picked, setPicked] = useState<readonly PickedMember[]>([]);
  const [emails, setEmails] = useState<readonly string[]>([]);
  const [emailDraft, setEmailDraft] = useState('');
  const [emailError, setEmailError] = useState<string | null>(null);
  const [emailMatches, setEmailMatches] = useState<Record<string, EmailMatchState>>({});
  // Unified per-batch role. Applies to both picked-member invites and typed
  // emails — the server's `createGroupInvites` POST takes one `roleOnAccept`
  // for the whole batch.
  const [roleOnAccept, setRoleOnAccept] = useState<'member' | 'leader'>('member');

  // Personal note is stored server-side per (viewer, group) via
  // GET/PUT /{kind}/{id}/invite-prefs. Local textarea state holds the
  // unsaved-draft value; the user commits via the "Save as default" button.
  const setInviteNote = useSetGroupInviteNote();
  // Parallel queries, each gated by `kind` via `enabled` (plus `open`).
  // `circle.inviteNote` and `network.inviteNote` are structurally identical
  // leaves differing only by a `'circle' | 'network'` key discriminator;
  // consuming them as one ternary yields a union TS can't unify at `useQuery`,
  // so we run both and read whichever is live.
  const circleNoteQuery = useQuery({
    ...groupKeys.circle.inviteNote(groupId),
    enabled: open && kind === 'circle',
  });
  const networkNoteQuery = useQuery({
    ...groupKeys.network.inviteNote(groupId),
    enabled: open && kind === 'network',
  });
  const inviteNoteQuery = kind === 'circle' ? circleNoteQuery : networkNoteQuery;
  const savedNote = inviteNoteQuery.data ?? '';
  const [note, setNote] = useState('');
  useEffect(() => {
    setNote(savedNote);
  }, [savedNote]);
  const noteDirty = note !== savedNote;
  const [noteSaveError, setNoteSaveError] = useState<string | null>(null);

  // One-time migration from the previous localStorage scheme. If a value
  // exists locally and the server has none, push it once then drop the
  // localStorage key. Safe to delete after a few releases.
  const noteStorageKey = `invite-note:${kind}:${groupId}`;
  useEffect(() => {
    if (!open) return;
    if (inviteNoteQuery.data === undefined) return;
    if (typeof window === 'undefined') return;
    if (inviteNoteQuery.data.length > 0) return;
    const local = readStorage('local', noteStorageKey);
    if (local === null || local.length === 0) return;
    // mutateAsync's promise settles regardless of the mutation observer's
    // lifecycle; a per-call onSuccess here would be silently dropped when the
    // observer recycles mid-flight (StrictMode's simulated remount), leaving
    // the legacy key stranded forever — the server copy then suppresses any
    // retry via the length guard above.
    void setInviteNote
      .mutateAsync({ kind, groupId, note: local })
      .then(() => {
        removeStorage('local', noteStorageKey);
      })
      .catch(() => {
        // Migration failure keeps the local key; the next open retries.
      });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, inviteNoteQuery.data, noteStorageKey, kind, groupId]);

  const [noteJustSaved, setNoteJustSaved] = useState(false);
  const noteSavedFlashRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const sendInFlightRef = useRef(false);
  useEffect(
    () => () => {
      if (noteSavedFlashRef.current !== null) clearTimeout(noteSavedFlashRef.current);
    },
    [],
  );

  const onSaveNoteAsDefault = () => {
    setNoteSaveError(null);
    setInviteNote.mutate(
      { kind, groupId, note },
      {
        onSuccess: () => {
          setNoteJustSaved(true);
          if (noteSavedFlashRef.current !== null) {
            clearTimeout(noteSavedFlashRef.current);
          }
          noteSavedFlashRef.current = setTimeout(() => setNoteJustSaved(false), 2000);
        },
        onError: (err) =>
          setNoteSaveError(describeError(err, t('invite.noteField.saveError'))),
      },
    );
  };

  const [search, setSearch] = useState('');
  const [hits, setHits] = useState<readonly TypeaheadHit[]>([]);

  const [error, setError] = useState<string | null>(null);
  const [discardOpen, setDiscardOpen] = useState(false);
  const isSending = sendInvitesMutation.isPending;

  useResetOnClose(open, () => {
    setStep('compose');
    setResult(null);
    setPicked([]);
    setEmails([]);
    setEmailDraft('');
    setEmailError(null);
    setEmailMatches({});
    setRoleOnAccept('member');
    // `note` deliberately preserved — see noteStorageKey above.
    setSearch('');
    setHits([]);
    setError(null);
    setDiscardOpen(false);
  });

  // Debounced typeahead.
  useEffect(() => {
    if (!open) return;
    const q = search.trim();
    if (q.length === 0) {
      setHits([]);
      return;
    }
    let cancelled = false;
    const handle = setTimeout(() => {
      void fetchInvitableMembers({ kind, groupId, q, limit: 8 }).then((rows) => {
        if (cancelled) return;
        // Rollout safety: an API instance from the previous deploy may omit
        // `relationship`. Default it to `invitable` at the boundary so the
        // downstream reads (disabled flag, selection guard, status badge)
        // never dereference `undefined.kind` during a mixed-version window.
        // The type says `relationship` is required; the cast reflects that the
        // wire shape isn't guaranteed across a deploy.
        setHits(
          rows.map((r) => {
            const rel = (r as { relationship?: TypeaheadHit['relationship'] }).relationship;
            return { ...r, relationship: rel ?? { kind: 'invitable' as const } };
          }),
        );
      });
    }, SEARCH_DEBOUNCE_MS);
    return () => {
      cancelled = true;
      clearTimeout(handle);
    };
  }, [open, search, kind, groupId]);

  const pickedIds = useMemo(() => new Set(picked.map((p) => p.userId)), [picked]);
  const visibleHits = useMemo(
    () => hits.filter((h) => !pickedIds.has(h.userId)),
    [hits, pickedIds],
  );

  const pickerOptions = useMemo<ReadonlyArray<ComboboxOption<string>>>(
    () =>
      visibleHits.map((h) => ({
        value: h.userId,
        label: fullName(h.firstName, h.lastName),
        // Already a member / already invited → shown for feedback but not
        // selectable. The picker dims the row and ignores clicks/Enter.
        ...(h.relationship.kind !== 'invitable' ? { disabled: true } : {}),
      })),
    [visibleHits],
  );
  const pickerSelectedOptions = useMemo<ReadonlyArray<ComboboxOption<string>>>(
    () => picked.map((p) => ({ value: p.userId, label: p.name })),
    [picked],
  );

  const commitEmail = () => {
    const raw = emailDraft.trim().replace(/,$/, '').trim();
    if (raw.length === 0) {
      setEmailError(null);
      return;
    }
    if (!isValidEmailAddress(raw)) {
      setEmailError(t('invite.email.invalid'));
      return;
    }
    if (emails.some((e) => e.toLowerCase() === raw.toLowerCase())) {
      setEmailDraft('');
      setEmailError(t('invite.email.alreadyAdded', { email: raw }));
      return;
    }
    setEmails((prev) => [...prev, raw]);
    setEmailDraft('');
    setEmailError(null);
    void lookupEmailMatch(raw);
  };

  const removeEmail = (e: string) => {
    setEmails((prev) => prev.filter((x) => x !== e));
    setEmailMatches((prev) => {
      const { [e.toLowerCase()]: _drop, ...rest } = prev;
      return rest;
    });
  };

  const lookupEmailMatch = async (rawEmail: string) => {
    const key = rawEmail.toLowerCase();
    setEmailMatches((prev) => ({ ...prev, [key]: 'pending' }));
    try {
      const status = await fetchInviteEmailStatus({
        kind,
        groupId,
        email: rawEmail,
      });
      setEmailMatches((prev) => ({ ...prev, [key]: projectStatus(status) }));
    } catch {
      setEmailMatches((prev) => ({ ...prev, [key]: { kind: 'error' } }));
    }
  };

  const onEmailKeyDown = (ev: KeyboardEvent<HTMLInputElement>) => {
    if (ev.key === 'Enter' || ev.key === ',') {
      ev.preventDefault();
      commitEmail();
    } else if (ev.key === 'Backspace' && emailDraft.length === 0 && emails.length > 0) {
      ev.preventDefault();
      const last = emails[emails.length - 1];
      if (last !== undefined) removeEmail(last);
    }
  };

  const onEmailPaste = (ev: ClipboardEvent<HTMLInputElement>) => {
    const tokens = splitPastedRecipients(ev.clipboardData.getData('text'));
    const valid = tokens.filter(isValidEmailAddress);
    // No committable address in the paste — let it land in the draft so the
    // existing commit-time validation handles it.
    if (valid.length === 0) return;
    ev.preventDefault();
    const rejected = tokens.filter((t) => !isValidEmailAddress(t));
    const present = new Set(emails.map((e) => e.toLowerCase()));
    const fresh: string[] = [];
    const duplicates: string[] = [];
    for (const v of valid) {
      const key = v.toLowerCase();
      if (present.has(key)) {
        duplicates.push(v);
        continue;
      }
      present.add(key);
      fresh.push(v);
    }
    if (fresh.length > 0) {
      setEmails((prev) => [...prev, ...fresh]);
      for (const f of fresh) void lookupEmailMatch(f);
    }
    // The half-typed draft is untouched — the paste became chips, not text.
    // Account for every pasted token — an all-duplicates paste that adds
    // nothing and says nothing reads as "paste is broken".
    const parts: string[] = [];
    if (rejected.length > 0) {
      parts.push(
        t('invite.email.pasteRejected', {
          count: rejected.length,
          list: rejected.join(', '),
        }),
      );
    }
    if (duplicates.length > 0) {
      parts.push(
        duplicates.length === 1
          ? t('invite.email.alreadyAdded', { email: duplicates[0]! })
          : t('invite.email.pasteDuplicatesMany', { list: duplicates.join(', ') }),
      );
    }
    setEmailError(parts.length > 0 ? parts.join(' ') : null);
  };

  // Non-blocking emails (the ones the Send button will actually submit).
  // Picked members from the typeahead are excluded from this set because
  // they are always invitable — the picker surfaces already-members and
  // pending-invite recipients but renders them non-selectable, so only
  // `invitable` rows can ever reach `picked`.
  const sendableEmails = useMemo(
    () => emails.filter((e) => !isBlockingState(emailMatches[e.toLowerCase()], roleOnAccept)),
    [emails, emailMatches, roleOnAccept],
  );
  const hasInvalid = emails.some((e) => {
    const s = emailMatches[e.toLowerCase()];
    return s !== undefined && s !== 'pending' && s.kind === 'invalid_email';
  });

  const sendCount = picked.length + new Set(sendableEmails.map((e) => e.toLowerCase())).size;
  const overCap = sendCount > INVITE_BATCH_CAP;

  const onSend = () => {
    // Double-submit guard. A same-tick double-click fires onSend twice
    // before React commits `isPending` (or the disabled button) — both
    // calls read the stale state — so the async mutation flag can't stop
    // the second POST. A ref flips synchronously and does. Cleared in the
    // mutation's onSettled below.
    if (sendInFlightRef.current) return;
    setError(null);
    const pickedEmails = picked.map((p) => p.email);
    const allEmails = Array.from(
      new Set([...pickedEmails, ...sendableEmails].map((e) => e.toLowerCase())),
    );
    if (allEmails.length === 0) {
      setError(t('invite.sendError.noRecipients'));
      return;
    }
    // Latched only once the attempt actually starts: every path that sets
    // the ref also arms the onSettled that clears it. Latching earlier let
    // an aborted attempt (a return above) leave it set forever, turning
    // every future Send into a silent no-op.
    sendInFlightRef.current = true;
    sendInvitesMutation.mutate(
      {
        kind,
        groupId,
        emails: allEmails,
        roleOnAccept,
        ...(note.trim().length > 0 ? { note: note.trim() } : {}),
      },
      {
        onSuccess: (data) => {
          setResult(data);
          setStep('result');
        },
        onError: (err) => {
          // The shared address validator keeps client chips and the server
          // schema in lockstep, so a VALIDATION rejection here means drift —
          // flag the exact offending chips rather than failing opaquely.
          if (err instanceof DomainError && err.error.code === 'VALIDATION') {
            const bad = invalidEmailsFromDetails(err.error.details, allEmails);
            if (bad.length > 0) {
              setEmailMatches((prev) => {
                const next = { ...prev };
                for (const b of bad) next[b] = { kind: 'invalid_email' };
                return next;
              });
              setError(
                bad.length === 1
                  ? t('invite.sendError.validationSingle', { email: bad[0]! })
                  : t('invite.sendError.validationMany', { list: bad.join(', ') }),
              );
              return;
            }
            setError(t('invite.sendError.validationGeneric'));
            return;
          }
          setError(sendFailureMessage(err, describeError, t));
        },
        onSettled: () => {
          sendInFlightRef.current = false;
        },
      },
    );
  };

  const onSendMore = () => {
    setStep('compose');
    setResult(null);
    setPicked([]);
    setEmails([]);
    setEmailDraft('');
    setEmailError(null);
    setEmailMatches({});
    setRoleOnAccept('member');
    setError(null);
  };

  const requestClose = () => {
    // No dismissal while the batch send is in flight: the invitations are
    // already going out server-side, so the "discard?" dialog this would
    // open is a lie, and closing would wipe the per-recipient result screen
    // the resolving send is about to show. Same synchronous ref as the
    // double-submit guard in onSend — render state commits too late to stop
    // a same-tick Escape.
    if (sendInFlightRef.current) return;
    if (step === 'result') {
      onOpenChange(false);
      return;
    }
    const dirty = picked.length > 0 || emails.length > 0;
    if (dirty) {
      setDiscardOpen(true);
      return;
    }
    onOpenChange(false);
  };

  const confirmDiscard = () => {
    onOpenChange(false);
  };

  return (
    <>
      <Dialog
        open={open}
        onOpenChange={(next) => {
          if (!next) {
            requestClose();
            return;
          }
          onOpenChange(next);
        }}
        size="lg"
        accessibleTitle={t('invite.accessibleTitle', { groupName })}
      >
        <DialogSection bleed>
          <Row justify="space-between" align="center" gap="lg" py="lg" px="lg">
            <Heading level={3}>
              {step === 'compose'
                ? t('invite.headingCompose', { groupName })
                : t('invite.headingResult')}
            </Heading>
            <Button
              variant="ghost"
              size="sm"
              icon={I.x}
              aria-label={t('invite.close')}
              onClick={requestClose}
            />
          </Row>
        </DialogSection>

        {step === 'compose' ? (
          <DialogSection scrollable divider>
            <Stack gap="lg">
              <Row
                justify="space-between"
                align="center"
                gap="md"
                pb="lg"
                style={{ borderBottom: '1px solid var(--border)' }}
              >
                <Body size="sm" color="mute">
                  {t('invite.joinLinkRow')}
                </Body>
                <CopyJoinLinkButton groupId={groupId} />
              </Row>
              <Field
                label={t('invite.memberField.label')}
                description={t('invite.memberField.description')}
              >
                <MultiCombobox<string>
                  ariaLabel={t('invite.memberField.ariaLabel')}
                  placeholder={t('invite.memberField.placeholder')}
                  options={pickerOptions}
                  value={picked.map((p) => p.userId)}
                  selectedItems={pickerSelectedOptions}
                  onChange={(nextIds) => {
                    const idToHit = new Map(visibleHits.map((h) => [h.userId, h] as const));
                    const idToPicked = new Map(picked.map((p) => [p.userId, p] as const));
                    const nextPicked: PickedMember[] = [];
                    for (const id of nextIds) {
                      const existing = idToPicked.get(id);
                      if (existing !== undefined) {
                        nextPicked.push(existing);
                        continue;
                      }
                      const hit = idToHit.get(id);
                      // Defense in depth: the picker already blocks selecting a
                      // disabled (non-invitable) row, but never trust the id list.
                      if (hit !== undefined && hit.relationship.kind === 'invitable') {
                        nextPicked.push({
                          userId: hit.userId,
                          email: hit.email,
                          name: fullName(hit.firstName, hit.lastName),
                          photoUrl: hit.photoUrl,
                          jobTitle: hit.jobTitle,
                          company: hit.company,
                        });
                      }
                    }
                    setPicked(nextPicked);
                    setSearch('');
                    setHits([]);
                  }}
                  onQueryChange={setSearch}
                  noInternalFilter
                  emptyLabel={
                    search.trim().length === 0
                      ? t('invite.memberField.emptyTyping')
                      : t('invite.memberField.emptyNoMatches')
                  }
                  renderOption={(opt) => {
                    const hit = visibleHits.find((h) => h.userId === opt.value);
                    if (hit === undefined) return null;
                    const name = fullName(hit.firstName, hit.lastName);
                    const sub = formatPersonRole(hit.jobTitle, hit.company);
                    const status = relationshipStatus(hit.relationship, kind, t);
                    return (
                      <Row align="center" gap="sm" style={{ width: '100%' }}>
                        <div style={{ flex: 1, minWidth: 0 }}>
                          <PersonRow
                            {...(hit.photoUrl ? { photo: hit.photoUrl } : {})}
                            name={name}
                            avatarSize={32}
                            {...(sub.length > 0 ? { sublabel: sub } : {})}
                          />
                        </div>
                        {status !== null ? (
                          <Tooltip content={status.tooltip}>
                            <Row gap="xs" align="center" style={{ flexShrink: 0 }}>
                              <Icon size={12} color="var(--muted-foreground)">
                                {status.glyph}
                              </Icon>
                              <Body size="sm" color="mute" as="span">
                                {status.label}
                              </Body>
                            </Row>
                          </Tooltip>
                        ) : null}
                      </Row>
                    );
                  }}
                  renderChip={(opt, { onRemove }) => {
                    const member = picked.find((p) => p.userId === opt.value);
                    if (member === undefined) return null;
                    return <PickedChip member={member} onRemove={onRemove} />;
                  }}
                />
              </Field>

              <Stack gap="xs">
                <Checkbox
                  checked={roleOnAccept === 'leader'}
                  onCheckedChange={(v) => setRoleOnAccept(v === true ? 'leader' : 'member')}
                  label={t('invite.leaderToggle.label')}
                />
                <Body size="sm" color="mute">
                  {roleOnAccept === 'leader'
                    ? t('invite.leaderToggle.helperLeader', { kind })
                    : t('invite.leaderToggle.helperMember')}
                </Body>
              </Stack>

              <Field>
                <Field.Label>{t('invite.emailField.label')}</Field.Label>
                <Stack gap="sm">
                  {emails.length > 0 ? (
                    <Row gap="sm" wrap>
                      {emails.map((e) => (
                        <EmailChip
                          key={e}
                          email={e}
                          state={emailMatches[e.toLowerCase()]}
                          kind={kind}
                          roleOnAccept={roleOnAccept}
                          onRemove={() => removeEmail(e)}
                        />
                      ))}
                    </Row>
                  ) : null}
                  <Field.Control>
                    <Input
                      type="email"
                      value={emailDraft}
                      placeholder={t('invite.emailField.placeholder')}
                      onChange={(e) => setEmailDraft(e.target.value)}
                      onKeyDown={onEmailKeyDown}
                      onPaste={onEmailPaste}
                      onBlur={commitEmail}
                      invalid={emailError !== null}
                    />
                  </Field.Control>
                  {overCap ? (
                    <Notice
                      intent="warning"
                      placement="inline"
                      title={t('invite.overCapTitle', {
                        count: sendCount,
                        cap: INVITE_BATCH_CAP,
                      })}
                      dismissible={false}
                    >
                      {t('invite.overCapBody')}
                    </Notice>
                  ) : null}
                  {(() => {
                    const summary = chipsSummary(emails, emailMatches, kind, roleOnAccept, t);
                    if (summary.skippedTotal === 0) return null;
                    return (
                      <Notice
                        intent="warning"
                        placement="inline"
                        title={t('invite.summaryTitle', { count: summary.skippedTotal })}
                        dismissible={false}
                        announceOn="transition"
                      >
                        {summary.skippedReasons.join(' · ')}.{' '}
                        {t('invite.summaryReady', { count: summary.ready })}
                      </Notice>
                    );
                  })()}
                </Stack>
                {emailError !== null ? <Field.Error>{emailError}</Field.Error> : null}
              </Field>

              <Field>
                <Field.Label>{t('invite.noteField.label')}</Field.Label>
                <Stack gap="sm">
                  <Field.Control>
                    <Textarea
                      rows={3}
                      value={note}
                      onChange={(e) => setNote(e.target.value)}
                      placeholder={t('invite.noteField.placeholder', { kind })}
                    />
                  </Field.Control>
                  <Row gap="sm" align="center" justify="space-between" wrap>
                    <Body size="sm" color="mute">
                      {noteDirty
                        ? savedNote.length > 0
                          ? t('invite.noteField.helperUnsaved')
                          : t('invite.noteField.helperBatchOnly')
                        : savedNote.length > 0
                          ? t('invite.noteField.helperSaved', { kind })
                          : null}
                    </Body>
                    <Button
                      variant={noteDirty ? 'primary' : 'secondary'}
                      size="sm"
                      onClick={onSaveNoteAsDefault}
                      disabled={!noteDirty}
                      loading={setInviteNote.isPending}
                      {...(!noteDirty && (noteJustSaved || savedNote.length > 0)
                        ? { icon: I.check }
                        : {})}
                    >
                      {noteJustSaved
                        ? t('invite.noteField.justSaved')
                        : !noteDirty && savedNote.length > 0
                          ? t('invite.noteField.saved')
                          : t('invite.noteField.save')}
                    </Button>
                  </Row>
                </Stack>
                <Field.Description>{t('invite.noteField.description')}</Field.Description>
                {noteSaveError !== null ? <Field.Error>{noteSaveError}</Field.Error> : null}
              </Field>
            </Stack>
          </DialogSection>
        ) : (
          <DialogSection scrollable divider>
            <ResultPanel result={result} kind={kind} />
          </DialogSection>
        )}

        {/* Pinned between the scrollable body and the footer so a send
          failure is always visible next to the button that triggered it —
          inside the scroll area it can sit off-screen and read as
          "nothing happened". */}
        {step === 'compose' && error !== null ? (
          <DialogSection divider>
            <Notice
              intent="danger"
              placement="inline"
              title={t('invite.errorNoticeTitle')}
              dismissible={false}
            >
              {error}
            </Notice>
          </DialogSection>
        ) : null}

        {step === 'compose' ? (
          <DialogFooter justify="space-between">
            <Row gap="sm" align="center" style={{ minWidth: 0 }}>
              <Icon size={14} color="var(--muted-foreground)">
                {I.shield}
              </Icon>
              <Body size="sm" color="mute">
                {t('invite.footer.privacy', { kind })}
              </Body>
            </Row>
            <Row gap="md" align="center" style={{ flexShrink: 0 }}>
              <Button variant="ghost" onClick={requestClose} disabled={isSending}>
                {t('invite.footer.cancel')}
              </Button>
              <Button
                variant="primary"
                iconRight={I.send}
                onClick={onSend}
                disabled={sendCount === 0 || hasInvalid || overCap}
                loading={isSending}
              >
                {t('invite.footer.send', { count: sendCount })}
              </Button>
            </Row>
          </DialogFooter>
        ) : (
          <DialogFooter justify="space-between">
            <Button variant="secondary" onClick={onSendMore}>
              {t('invite.footer.sendMore')}
            </Button>
            <Button variant="primary" onClick={() => onOpenChange(false)}>
              {t('invite.footer.done')}
            </Button>
          </DialogFooter>
        )}
      </Dialog>

      <AlertDialog
        open={discardOpen}
        onOpenChange={setDiscardOpen}
        title={t('invite.discard.title')}
        description={t('invite.discard.description')}
        actionLabel={t('invite.discard.action')}
        cancelLabel={t('invite.discard.cancel')}
        onAction={confirmDiscard}
      />
    </>
  );
}

function PickedChip({ member, onRemove }: { member: PickedMember; onRemove: () => void }) {
  const t = useTranslations('Group');
  const sub = formatPersonRole(member.jobTitle, member.company);
  return (
    <Row
      gap="sm"
      align="center"
      style={{
        // eslint-disable-next-line @leanin/ui/no-inline-spacing-vars -- pill-shaped chip with asymmetric padding (start vs end); no Box prop for asymmetric inline padding
        padding: 'var(--space-xs) var(--space-sm) var(--space-xs) var(--space-xs)',
        border: `1px solid ${TONE.neutral.bg}`,
        background: 'var(--muted)',
        borderRadius: 'var(--radius-full)',
        maxWidth: '100%',
      }}
    >
      {/* Avatar matches the 28px remove-button height (the chip's tallest
          child) so its inset is space-xs on all sides — concentric with the
          pill cap. A smaller avatar floats: 4px left, 7px top/bottom. */}
      <Avatar {...(member.photoUrl ? { src: member.photoUrl } : {})} name={member.name} size={28} />
      <span
        style={{
          font: 'var(--type-label)',
          color: 'var(--foreground)',
          whiteSpace: 'nowrap',
          overflow: 'hidden',
          textOverflow: 'ellipsis',
          maxWidth: 220,
        }}
      >
        {member.name}
        {sub.length > 0 ? <span style={{ color: 'var(--muted-foreground)' }}> · {sub}</span> : null}
      </span>
      <Button
        variant="ghost"
        size="sm"
        icon={I.x}
        aria-label={t('invite.chip.removeMember', { name: member.name })}
        onClick={onRemove}
      />
    </Row>
  );
}

// Per-chip visual + tooltip projected from the server's status probe.
// The chip itself encodes everything the leader needs: avatar/glyph slot,
// label, hover tooltip with the long-form reason. Industry-standard for
// bulk-invite modals (Slack, Linear, Notion, Stripe) — see design notes
// in PR. Pre-redesign we had a stacked `<email> — <long sentence>` row
// per chip plus a persistent helper paragraph; that scaled to ~3 chips
// before the modal turned into a wall of text.
// Leading slot is avatar-or-nothing: an avatar means "this email resolved
// to a real member" and is worth the space; generic type-glyphs (envelope,
// magnifier) said nothing and sat badly in the avatar-tuned padding. All
// state semantics live in the trailing status glyph + tone + tooltip.
type EmailChipVisual = {
  readonly label: string;
  readonly leading: 'avatar' | null;
  readonly statusGlyph: ReactNode | null;
  readonly tone: 'matched' | 'neutral' | 'muted' | 'destructive' | 'pending';
  readonly tooltip: string;
};

function chipVisual(
  email: string,
  state: EmailMatchState | undefined,
  kind: GroupKind,
  roleOnAccept: 'member' | 'leader',
  t: ReturnType<typeof useTranslations<'Group'>>,
): EmailChipVisual {
  if (state === undefined || state === 'pending') {
    return {
      label: email,
      leading: null,
      statusGlyph: null,
      tone: 'pending',
      tooltip: t('invite.chip.checking'),
    };
  }
  switch (state.kind) {
    case 'on_platform_invitable':
      return {
        label: state.name,
        leading: 'avatar',
        statusGlyph: null,
        tone: 'matched',
        tooltip:
          state.sublabel.length > 0
            ? t('invite.chip.matchedTooltip', { email, sublabel: state.sublabel })
            : email,
      };
    case 'off_platform':
      return {
        label: email,
        leading: null,
        statusGlyph: null,
        tone: 'neutral',
        tooltip: t('invite.chip.offPlatformTooltip'),
      };
    case 'already_member': {
      // Promote case: existing member, leader toggled "Invite as leader".
      // Chip stays full-tone (not skipped), shows an up-chevron, and the
      // tooltip names the role bump. Server applies the promote in the
      // same tx as the bulk-create call (no separate API hit).
      if (state.role === 'member' && roleOnAccept === 'leader') {
        return {
          label: state.name,
          leading: 'avatar',
          statusGlyph: <ArrowUpGlyph size={12} strokeWidth={2} />,
          tone: 'matched',
          tooltip: t('invite.chip.promoteTooltip', { kind }),
        };
      }
      return {
        label: state.name,
        leading: 'avatar',
        statusGlyph: I.check,
        tone: 'muted',
        tooltip: t('invite.relationship.alreadyTooltip', { role: state.role, kind }),
      };
    }
    case 'pending_invite':
      return {
        label: email,
        leading: null,
        statusGlyph: <ClockGlyph size={14} strokeWidth={1.75} />,
        tone: 'muted',
        tooltip:
          state.inviterName.length > 0
            ? t('invite.relationship.invitedByTooltip', {
                time: relativeDays(state.sentAtIso, t),
                name: state.inviterName,
              })
            : t('invite.relationship.invitedTooltip', {
                time: relativeDays(state.sentAtIso, t),
              }),
      };
    case 'pending_request':
      return {
        label: email,
        leading: null,
        statusGlyph: <ClockGlyph size={14} strokeWidth={1.75} />,
        tone: 'muted',
        tooltip: t('invite.chip.pendingRequestTooltip', {
          time: relativeDays(state.requestedAtIso, t),
        }),
      };
    case 'invalid_email':
      return {
        label: email,
        leading: null,
        statusGlyph: I.alert,
        tone: 'destructive',
        tooltip: t('invite.chip.invalidTooltip'),
      };
    case 'error':
      return {
        label: email,
        leading: null,
        statusGlyph: I.alert,
        tone: 'neutral',
        tooltip: t('invite.chip.errorTooltip'),
      };
  }
}

function EmailChip({
  email,
  state,
  kind,
  roleOnAccept,
  onRemove,
}: {
  email: string;
  state: EmailMatchState | undefined;
  kind: GroupKind;
  roleOnAccept: 'member' | 'leader';
  onRemove: () => void;
}) {
  const t = useTranslations('Group');
  const v = chipVisual(email, state, kind, roleOnAccept, t);
  const photoUrl =
    state !== undefined &&
    state !== 'pending' &&
    (state.kind === 'on_platform_invitable' || state.kind === 'already_member')
      ? state.photoUrl
      : null;
  const memberName =
    state !== undefined &&
    state !== 'pending' &&
    (state.kind === 'on_platform_invitable' || state.kind === 'already_member')
      ? state.name
      : email;

  const palette = (() => {
    switch (v.tone) {
      case 'matched':
        return {
          bg: 'var(--muted)',
          border: 'var(--border)',
          ink: 'var(--foreground)',
          opacity: 1,
        };
      case 'neutral':
      case 'pending':
        return {
          bg: 'var(--muted)',
          border: 'var(--border)',
          ink: 'var(--foreground)',
          opacity: 1,
        };
      case 'muted':
        return {
          bg: 'var(--muted)',
          border: 'var(--border)',
          ink: 'var(--muted-foreground)',
          opacity: 0.7,
        };
      case 'destructive':
        return {
          bg: 'var(--muted)',
          border: 'var(--destructive)',
          ink: 'var(--destructive)',
          opacity: 1,
        };
    }
  })();

  return (
    <Tooltip content={v.tooltip}>
      <Row
        gap="sm"
        align="center"
        style={{
          // eslint-disable-next-line @leanin/ui/no-inline-spacing-vars -- pill-shaped chip with asymmetric padding; left inset depends on whether an avatar sits flush against the rounded end
          padding: `var(--space-xs) var(--space-sm) var(--space-xs) ${v.leading === 'avatar' ? 'var(--space-xs)' : 'var(--space-md)'}`,
          border: `1px solid ${palette.border}`,
          background: palette.bg,
          borderRadius: 'var(--radius-full)',
          maxWidth: '100%',
          opacity: palette.opacity,
        }}
      >
        {v.leading === 'avatar' ? (
          // 28px = remove-button height, keeping the avatar inset at
          // space-xs on all sides (see PickedChip).
          <Avatar {...(photoUrl ? { src: photoUrl } : {})} name={memberName} size={28} />
        ) : null}
        <span
          style={{
            font: 'var(--type-label)',
            color: palette.ink,
            whiteSpace: 'nowrap',
            overflow: 'hidden',
            textOverflow: 'ellipsis',
            maxWidth: 220,
          }}
        >
          {v.label}
        </span>
        {v.statusGlyph !== null ? (
          <Icon
            size={12}
            color={v.tone === 'destructive' ? 'var(--destructive)' : 'var(--muted-foreground)'}
          >
            {v.statusGlyph}
          </Icon>
        ) : null}
        <Button
          variant="ghost"
          size="sm"
          icon={I.x}
          aria-label={t('invite.chip.removeEmail', { email })}
          onClick={onRemove}
        />
      </Row>
    </Tooltip>
  );
}

// One-line aggregate summary shown beneath the chip strip when at least one
// chip is in a blocking state. Industry-standard pattern — the chips
// themselves carry per-recipient state via visual + tooltip; this caption
// just totals what will actually go out vs what's being skipped. Send
// button's "Send N invites" count is the canonical ready-count.
function chipsSummary(
  emails: readonly string[],
  matches: Record<string, EmailMatchState>,
  kind: GroupKind,
  roleOnAccept: 'member' | 'leader',
  t: ReturnType<typeof useTranslations<'Group'>>,
): {
  readonly ready: number;
  readonly skippedTotal: number;
  readonly skippedReasons: readonly string[];
} {
  let ready = 0;
  let alreadyMember = 0;
  let pendingInvite = 0;
  let pendingRequest = 0;
  let invalid = 0;
  for (const e of emails) {
    const s = matches[e.toLowerCase()];
    if (s === undefined || s === 'pending') {
      ready += 1;
      continue;
    }
    switch (s.kind) {
      case 'on_platform_invitable':
      case 'off_platform':
      case 'error':
        ready += 1;
        break;
      case 'already_member':
        // Promote path is ready, not skipped — server bumps role in-tx.
        if (s.role === 'member' && roleOnAccept === 'leader') {
          ready += 1;
        } else {
          alreadyMember += 1;
        }
        break;
      case 'pending_invite':
        pendingInvite += 1;
        break;
      case 'pending_request':
        pendingRequest += 1;
        break;
      case 'invalid_email':
        invalid += 1;
        break;
    }
  }
  const skippedReasons: string[] = [];
  if (alreadyMember > 0) {
    skippedReasons.push(t('invite.summaryReason.alreadyInGroup', { count: alreadyMember, kind }));
  }
  if (pendingInvite > 0) {
    skippedReasons.push(t('invite.summaryReason.alreadyInvited', { count: pendingInvite }));
  }
  if (pendingRequest > 0) {
    skippedReasons.push(t('invite.summaryReason.alreadyRequested', { count: pendingRequest }));
  }
  if (invalid > 0) {
    skippedReasons.push(t('invite.summaryReason.invalid', { count: invalid }));
  }
  return {
    ready,
    skippedTotal: alreadyMember + pendingInvite + pendingRequest + invalid,
    skippedReasons,
  };
}

// Bucket the per-email outcomes into the five display groups. `invited` is the
// key semantic change from the pre-consent model: an existing user is now
// INVITED (a pending row awaiting THEIR acceptance), not added directly. Only
// `auto_accepted` (a matching request already existed → mutual consent) and
// `promoted` (already a member, role bumped in place) change membership
// immediately.
type OutcomeBuckets = {
  readonly invited: ReadonlyArray<Extract<PerEmailOutcome, { outcome: 'invited' }>>;
  readonly emailed: ReadonlyArray<Extract<PerEmailOutcome, { outcome: 'emailed' }>>;
  readonly joined: ReadonlyArray<Extract<PerEmailOutcome, { outcome: 'auto_accepted' }>>;
  readonly promoted: ReadonlyArray<Extract<PerEmailOutcome, { outcome: 'promoted' }>>;
  readonly alreadyMembers: ReadonlyArray<Extract<PerEmailOutcome, { outcome: 'skipped' }>>;
  readonly duplicates: ReadonlyArray<Extract<PerEmailOutcome, { outcome: 'skipped' }>>;
};

function bucketOutcomes(results: readonly PerEmailOutcome[]): OutcomeBuckets {
  const invited: Array<Extract<PerEmailOutcome, { outcome: 'invited' }>> = [];
  const emailed: Array<Extract<PerEmailOutcome, { outcome: 'emailed' }>> = [];
  const joined: Array<Extract<PerEmailOutcome, { outcome: 'auto_accepted' }>> = [];
  const promoted: Array<Extract<PerEmailOutcome, { outcome: 'promoted' }>> = [];
  const alreadyMembers: Array<Extract<PerEmailOutcome, { outcome: 'skipped' }>> = [];
  const duplicates: Array<Extract<PerEmailOutcome, { outcome: 'skipped' }>> = [];
  for (const r of results) {
    switch (r.outcome) {
      case 'invited':
        invited.push(r);
        break;
      case 'emailed':
        emailed.push(r);
        break;
      case 'auto_accepted':
        joined.push(r);
        break;
      case 'promoted':
        promoted.push(r);
        break;
      case 'skipped':
        if (r.reason === 'already_member') alreadyMembers.push(r);
        else duplicates.push(r);
        break;
    }
  }
  return { invited, emailed, joined, promoted, alreadyMembers, duplicates };
}

function ResultPanel({ result, kind }: { result: CreateInvitesResult | null; kind: GroupKind }) {
  const t = useTranslations('Group');
  if (result === null) {
    return (
      <Body size="sm" color="mute">
        {t('invite.result.noResult')}
      </Body>
    );
  }

  const { invited, emailed, joined, promoted, alreadyMembers, duplicates } = bucketOutcomes(
    result.results,
  );

  return (
    <Stack gap="lg">
      <Row gap="sm" wrap>
        {invited.length > 0 ? (
          <Pill size="md" tone="emerald">
            {t('invite.result.pillInvited', { count: invited.length })}
          </Pill>
        ) : null}
        {emailed.length > 0 ? (
          <Pill size="md" tone="emerald">
            {t('invite.result.pillEmailed', { count: emailed.length })}
          </Pill>
        ) : null}
        {joined.length > 0 ? (
          <Pill size="md" tone="emerald">
            {t('invite.result.pillJoined', { count: joined.length })}
          </Pill>
        ) : null}
        {promoted.length > 0 ? (
          <Pill size="md" tone="emerald">
            {t('invite.result.pillPromoted', { count: promoted.length })}
          </Pill>
        ) : null}
        {alreadyMembers.length > 0 ? (
          <Pill size="md" tone="neutral">
            {t('invite.result.pillAlreadyMembers', { count: alreadyMembers.length })}
          </Pill>
        ) : null}
        {duplicates.length > 0 ? (
          <Pill size="md" tone="neutral">
            {t('invite.result.pillDuplicates', { count: duplicates.length })}
          </Pill>
        ) : null}
      </Row>

      {invited.length > 0 ? (
        <ResultGroup
          title={t('invite.result.invitedTitle')}
          description={t('invite.result.invitedDescription', { kind })}
          tone="positive"
        >
          {invited.map((r) => (
            <ResultRow key={r.email} primary={r.email} />
          ))}
        </ResultGroup>
      ) : null}

      {joined.length > 0 ? (
        <ResultGroup
          title={t('invite.result.joinedTitle', { kind })}
          description={t('invite.result.joinedDescription')}
          tone="positive"
        >
          {joined.map((r) => (
            <ResultRow key={r.email} primary={r.email} />
          ))}
        </ResultGroup>
      ) : null}

      {promoted.length > 0 ? (
        <ResultGroup
          title={t('invite.result.promotedTitle')}
          description={t('invite.result.promotedDescription')}
          tone="positive"
        >
          {promoted.map((r) => (
            <ResultRow
              key={r.email}
              primary={fullName(r.firstName, r.lastName)}
              secondary={r.email}
            />
          ))}
        </ResultGroup>
      ) : null}

      {emailed.length > 0 ? (
        <ResultGroup
          title={t('invite.result.emailedTitle')}
          description={t('invite.result.emailedDescription')}
          tone="neutral"
        >
          {emailed.map((r) => (
            <ResultRow key={r.email} primary={r.email} />
          ))}
        </ResultGroup>
      ) : null}

      {alreadyMembers.length > 0 ? (
        <ResultGroup
          title={t('invite.result.alreadyMembersTitle', { kind })}
          description={t('invite.result.alreadyMembersDescription')}
          tone="muted"
        >
          {alreadyMembers.map((r) => (
            <ResultRow key={r.email} primary={r.email} />
          ))}
        </ResultGroup>
      ) : null}

      {duplicates.length > 0 ? (
        <ResultGroup
          title={t('invite.result.duplicatesTitle')}
          description={t('invite.result.duplicatesDescription')}
          tone="muted"
        >
          {duplicates.map((r) => (
            <ResultRow key={r.email} primary={r.email} />
          ))}
        </ResultGroup>
      ) : null}
    </Stack>
  );
}

function ResultGroup({
  title,
  description,
  tone,
  children,
}: {
  title: string;
  description: string;
  tone: 'positive' | 'neutral' | 'muted';
  children: React.ReactNode;
}) {
  const icon = tone === 'positive' ? I.check : tone === 'neutral' ? I.send : I.alert;
  const iconColor =
    tone === 'positive'
      ? 'var(--success)'
      : tone === 'neutral'
        ? 'var(--muted-foreground)'
        : 'var(--muted-foreground)';
  return (
    <Stack gap="sm">
      <Row gap="sm" align="center">
        <Icon size={14} color={iconColor}>
          {icon}
        </Icon>
        <Body size="md">
          <strong>{title}</strong>
        </Body>
      </Row>
      <Body size="sm" color="mute">
        {description}
      </Body>
      <Stack gap="xs">{children}</Stack>
    </Stack>
  );
}

function ResultRow({ primary, secondary }: { primary: string; secondary?: string }) {
  // `<Body>` defaults to `<p>`, which carries the browser's default top/bottom
  // margin. Inside this horizontal `<Row>` that margin manifests as a blank
  // line between the name and the email — render inline as `<span>` so the
  // two sit on one line at the row's natural baseline.
  return (
    <Row gap="sm" align="center">
      <Body size="sm" as="span">
        {primary}
      </Body>
      {secondary !== undefined ? (
        <Body size="sm" color="mute" as="span">
          · {secondary}
        </Body>
      ) : null}
    </Row>
  );
}
